import React, { useCallback, useRef, useState } from "react";
import {
  CheckCircle2,
  ImagePlus,
  Loader2,
  Plus,
  Sparkles,
  Star,
  Trash2,
  Wand2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { cn } from "@/lib/utils";

export const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB (same as classic builder)
export const MAX_FEATURES = 10;
export const MAX_FEATURE_NAME_LENGTH = 30;
export const MAX_DESCRIPTION_LENGTH = 500;
export const MAX_GENERATION_ATTEMPTS = 3;

export interface QuizPhoto {
  id: string;
  url: string;
  file?: File;
}

export const makePhotoId = () =>
  `photo_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

/** Same persisted-url rule as the classic step components. */
export const persistedUrl = (url?: string | null) => {
  if (typeof url !== "string") return "";
  const t = url.trim();
  if (!t || t.startsWith("blob:")) return "";
  return t;
};

/* ───────────────────────── Photo picker ───────────────────────── */

interface PhotoPickerProps {
  photos: QuizPhoto[];
  max: number;
  onChange: (next: QuizPhoto[]) => void;
  /** Called after the user applies an AI-enhanced version of a photo. */
  onEnhanced?: (payload: {
    id: string;
    originalUrl: string;
    enhancedUrl: string;
    isHero: boolean;
  }) => void;
}

export const PhotoPicker: React.FC<PhotoPickerProps> = ({
  photos,
  max,
  onChange,
  onEnhanced,
}) => {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [enhanceTarget, setEnhanceTarget] = useState<QuizPhoto | null>(null);
  const [enhancedUrl, setEnhancedUrl] = useState<string | null>(null);
  const [enhancing, setEnhancing] = useState(false);
  const [enhancedIds, setEnhancedIds] = useState<Set<string>>(new Set());

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      const slots = max - photos.length;
      if (slots <= 0) {
        toast({
          title: "Maximum Photos Reached",
          description: `You can only upload up to ${max} photo${max > 1 ? "s" : ""}.`,
          variant: "destructive",
        });
        return;
      }
      const accepted: QuizPhoto[] = [];
      list.slice(0, slots).forEach((file) => {
        if (!/\.(jpe?g|png)$/i.test(file.name || "")) {
          toast({
            title: "Not Supported",
            description: `${file.name} is not supported. Only JPG, JPEG, PNG allowed.`,
            variant: "destructive",
          });
          return;
        }
        if (!file.type.startsWith("image/")) {
          toast({
            title: "Invalid File Type",
            description: `${file.name} is not an image file.`,
            variant: "destructive",
          });
          return;
        }
        if (file.size > MAX_IMAGE_SIZE) {
          toast({
            title: "File Too Large",
            description: `${file.name} (${(file.size / 1048576).toFixed(2)} MB) exceeds 5 MB limit.`,
            variant: "destructive",
          });
          return;
        }
        accepted.push({ id: makePhotoId(), url: URL.createObjectURL(file), file });
      });
      if (accepted.length) onChange([...photos, ...accepted]);
      if (list.length > slots) {
        toast({
          title: "Some Photos Skipped",
          description: `Only ${max} photo${max > 1 ? "s are" : " is"} allowed.`,
          variant: "destructive",
        });
      }
    },
    [photos, max, onChange, toast]
  );

  const remove = (id: string) => {
    const p = photos.find((x) => x.id === id);
    if (p?.file && p.url.startsWith("blob:")) URL.revokeObjectURL(p.url);
    onChange(photos.filter((x) => x.id !== id));
  };

  const startEnhance = async (photo: QuizPhoto) => {
    if (!photo.file || enhancedIds.has(photo.id)) return;
    setEnhanceTarget(photo);
    setEnhancedUrl(null);
    setEnhancing(true);
    try {
      const fd = new FormData();
      fd.append("image", photo.file);
      const res = await axiosInstance.post(endpoints.enhance_image, fd);
      if (res.data?.success && res.data?.enhanced_image) {
        setEnhancedUrl(res.data.enhanced_image);
      } else {
        throw new Error("no image");
      }
    } catch (err: any) {
      toast({
        title: "Enhancement Failed",
        description: err?.response?.data?.error || "Please try again later.",
        variant: "destructive",
      });
      setEnhanceTarget(null);
    } finally {
      setEnhancing(false);
    }
  };

  const applyEnhance = () => {
    if (!enhanceTarget || !enhancedUrl) return;
    const isHero = photos[0]?.id === enhanceTarget.id;
    onChange(
      photos.map((p) => (p.id === enhanceTarget.id ? { ...p, url: enhancedUrl } : p))
    );
    onEnhanced?.({
      id: enhanceTarget.id,
      originalUrl: enhanceTarget.url,
      enhancedUrl,
      isHero,
    });
    setEnhancedIds((prev) => new Set(prev).add(enhanceTarget.id));
    toast({ title: "Image Enhanced ✨", description: "AI enhancement applied!" });
    setEnhanceTarget(null);
    setEnhancedUrl(null);
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
        multiple={max > 1}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {photos.length < max && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
          }}
          className={cn(
            "flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed bg-white p-8 transition",
            dragging
              ? "border-blue-500 bg-blue-50"
              : "border-slate-300 hover:border-blue-400 hover:bg-blue-50/40"
          )}
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow">
            <ImagePlus className="h-7 w-7" />
          </span>
          <span className="text-base font-semibold text-slate-900">
            Drop photos here, or click to browse
          </span>
          <span className="text-sm text-slate-500">
            JPG or PNG · up to 5 MB each · {photos.length}/{max} added · first photo is the main image
          </span>
        </button>
      )}

      {photos.length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((p, i) => (
            <div
              key={p.id}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
            >
              <img src={p.url} alt="" className="aspect-[4/3] w-full object-cover" />
              {i === 0 && (
                <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">
                  <Star className="h-3 w-3" /> Main
                </span>
              )}
              {enhancedIds.has(p.id) && (
                <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">
                  <CheckCircle2 className="h-3 w-3" /> Enhanced
                </span>
              )}
              <div className="absolute right-2 top-2 flex gap-1">
                {p.file && !enhancedIds.has(p.id) && (
                  <button
                    type="button"
                    onClick={() => startEnhance(p)}
                    title="Enhance with AI"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-violet-600 shadow hover:bg-white"
                  >
                    <Wand2 className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  title="Remove"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-red-600 shadow hover:bg-white"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={!!enhanceTarget}
        onOpenChange={(o) => {
          if (!o && !enhancing) {
            setEnhanceTarget(null);
            setEnhancedUrl(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" /> AI Image Enhancement
            </DialogTitle>
            <DialogDescription>Compare the original with the enhanced version.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 text-sm font-semibold text-slate-700">Original</p>
              {enhanceTarget && (
                <img src={enhanceTarget.url} alt="" className="w-full rounded-xl border object-contain" />
              )}
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold text-slate-700">Enhanced</p>
              {enhancing ? (
                <div className="flex aspect-[4/3] items-center justify-center rounded-xl border bg-slate-50">
                  <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
                </div>
              ) : enhancedUrl ? (
                <img src={enhancedUrl} alt="" className="w-full rounded-xl border object-contain" />
              ) : null}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              disabled={enhancing}
              onClick={() => {
                setEnhanceTarget(null);
                setEnhancedUrl(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={applyEnhance} disabled={!enhancedUrl || enhancing}>
              Apply enhancement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ───────────────────────── Feature list ───────────────────────── */

interface FeatureEditorProps {
  features: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
}

export const FeatureEditor: React.FC<FeatureEditorProps> = ({
  features,
  onChange,
  suggestions = [],
}) => {
  const [draft, setDraft] = useState("");
  const { toast } = useToast();
  const add = (raw: string) => {
    const name = raw.trim().slice(0, MAX_FEATURE_NAME_LENGTH);
    if (!name) return;
    if (features.length >= MAX_FEATURES) {
      toast({
        title: "Feature Limit Reached",
        description: `You can add a maximum of ${MAX_FEATURES} features.`,
        variant: "destructive",
      });
      return;
    }
    if (features.some((f) => f.toLowerCase() === name.toLowerCase())) return;
    onChange([...features, name]);
    setDraft("");
  };
  const available = suggestions.filter(
    (s) => !features.some((f) => f.toLowerCase() === s.toLowerCase())
  );
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between text-sm">
        <span className="font-medium text-slate-700">Key features</span>
        <span
          className={cn(
            "font-medium",
            features.length >= MAX_FEATURES ? "text-orange-600" : "text-slate-500"
          )}
        >
          {features.length}/{MAX_FEATURES}
        </span>
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          maxLength={MAX_FEATURE_NAME_LENGTH}
          placeholder="e.g. Pool, Renovated kitchen…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(draft);
            }
          }}
          disabled={features.length >= MAX_FEATURES}
          className="h-11"
        />
        <Button
          type="button"
          onClick={() => add(draft)}
          disabled={!draft.trim() || features.length >= MAX_FEATURES}
          className="h-11"
        >
          <Plus className="mr-1 h-4 w-4" /> Add
        </Button>
      </div>
      {features.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {features.map((f, i) => (
            <span
              key={`${f}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 py-1.5 pl-3 pr-2 text-sm font-medium text-blue-800"
            >
              {f}
              <button
                type="button"
                onClick={() => onChange(features.filter((_, idx) => idx !== i))}
                className="rounded-full p-0.5 hover:bg-blue-100"
                aria-label={`Remove ${f}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      {available.length > 0 && features.length < MAX_FEATURES && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
            Quick add
          </p>
          <div className="flex flex-wrap gap-2">
            {available.slice(0, 8).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => add(s)}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
              >
                + {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ───────────────────────── AI helper buttons ───────────────────────── */

interface AiButtonProps {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  tone?: "violet" | "slate";
}

export const AiButton: React.FC<AiButtonProps> = ({
  label,
  onClick,
  loading,
  disabled,
  tone = "violet",
}) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    onClick={onClick}
    disabled={disabled || loading}
    className={cn(
      "h-9 rounded-full",
      tone === "violet"
        ? "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"
        : "border-slate-200 bg-white text-slate-700"
    )}
  >
    {loading ? (
      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
    ) : (
      <Sparkles className="mr-1.5 h-4 w-4" />
    )}
    {label}
  </Button>
);

/* ───────────────────────── Field wrapper ───────────────────────── */

export const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
  hint?: string;
}> = ({ children, required, hint }) => (
  <div className="mb-1.5">
    <span className="text-sm font-semibold text-slate-800">
      {children}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </span>
    {hint && <span className="ml-2 text-xs text-slate-500">{hint}</span>}
  </div>
);

/** Strip everything except digits (used for number-only fields). */
export const digitsOnly = (v: string) => v.replace(/[^0-9]/g, "");
